import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { AudioPlayer } from 'src/meetings/detail/tabs/transcript/AudioPlayer';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import { useDocumentAudioPlayer } from '../hooks/useDocumentAudioPlayer';
import { isAudioFile } from './allowedFileTypes';
import FileUploadLayout from './FileUploadLayout';
import FiledDocumentInlineActions from './components/FiledDocumentInlineActions';
import RenameDocumentControl from './components/RenameDocumentControl';
import FiledDocumentsHeaderActions from './components/FiledDocumentsHeaderActions';
import SelectionHeaderActions from './components/SelectionHeaderActions';
import { useFiledDocumentsView } from './hooks/useFiledDocumentsView';
import { useBulkDocumentSelection } from './hooks/useBulkDocumentSelection';
import { useSharepointUpload } from './hooks/useSharepointUpload';
import BulkDeleteConfirmModal from './components/BulkDeleteConfirmModal';
import { downloadViaAnchor } from './utils/downloadViaAnchor';
import FilesTable from './files-table/FilesTable';
import { filedDocumentColumns } from './files-table/filedDocumentColumns';
import { buildFiledDocumentRowActions } from './files-table/filedDocumentRowActions';
import { CreateFolderModal } from './modals/CreateFolderModal';
import { RenameFolderModal } from './modals/RenameFolderModal';
import MoveFileModal from './files-table/MoveFileModal';
import { buildClientRootRecords, CategoryRow, filterFilesByScope } from './crossEntityFolders';
import { buildFileTree, filterTreeIds, flattenVisible, initialExpandedIds } from './fileTree';
import type { FlatRow } from './fileTree';

export default function FilesPage() {
  const { sectionSlug, folderId } = useParams<{ sectionSlug?: string; folderId?: string }>();

  const navigate = useNavigate();
  const { clientId, client } = useCurrentClient();
  const { flagEnabledSharepointUpload } = useFlags();

  const familyId = client?.family?.id ?? client?.familyId;
  const hasFamily = !!familyId;

  const [createFolderOpened, { open: openCreateFolder, close: closeCreateFolder }] =
    useDisclosure(false);
  const [createFolderParent, setCreateFolderParent] = useState<UploadedFile | null>(null);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState<UploadedFile | null>(null);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);

  const {
    files,
    allOtherFiles,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    createFolder,
    moveFile,
    renameFolder,
    isLoading: isLoadingFiles,
    error,
    refetchFiles,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });

  const {
    files: familyFiles,
    allOtherFiles: familyAllOtherFiles,
    isLoading: isLoadingFamilyFiles,
  } = useFileUploads({
    familyId: hasFamily ? familyId : undefined,
  });

  const familyOwnedFiles = hasFamily
    ? filterFilesByScope(familyFiles, { kind: 'family-shared' })
    : [];

  const familyAllOtherIds = hasFamily
    ? new Set(familyAllOtherFiles.map(f => f.id))
    : new Set<string>();
  const scopedFamilyFiles = familyOwnedFiles.filter(f => familyAllOtherIds.has(f.id));

  useEffect(() => {
    refetchFiles();
  }, [sectionSlug, folderId]);

  const { searchValue, setSearchValue, searchOpen, setSearchOpen, filteredFiles } =
    useFiledDocumentsView({
      files,
      allOtherFiles,
      currentFolder: null,
      routeKey: `${sectionSlug ?? ''}`,
    });

  const box1Tree = buildFileTree(allOtherFiles);

  const clientRootItemsForBox2 = hasFamily ? buildClientRootRecords(files, true) : null;
  const familySharedRow = clientRootItemsForBox2
    ? (clientRootItemsForBox2.find(
        (r): r is CategoryRow =>
          'categoryScope' in r && (r as CategoryRow).categoryScope.kind === 'family-shared',
      ) ?? undefined)
    : undefined;

  const box2Tree =
    hasFamily && familySharedRow
      ? buildFileTree([], [{ row: familySharedRow, files: scopedFamilyFiles }])
      : [];

  const [box1ExpandedIds, setBox1ExpandedIds] = useState<Set<string>>(new Set<string>());
  const box1SeededRef = useRef(false);
  useEffect(() => {
    if (box1SeededRef.current) return;
    if (box1Tree.length === 0) return;
    box1SeededRef.current = true;
    setBox1ExpandedIds(initialExpandedIds(box1Tree));
  }, [box1Tree]);

  // Guard on isLoadingFamilyFiles so the seed fires only once family data has resolved.
  const [box2ExpandedIds, setBox2ExpandedIds] = useState<Set<string>>(new Set<string>());
  const box2SeededRef = useRef(false);
  useEffect(() => {
    if (!hasFamily) return;
    if (box2SeededRef.current) return;
    if (box2Tree.length === 0) return;
    if (isLoadingFamilyFiles) return;
    box2SeededRef.current = true;

    const ids = new Set<string>();
    for (const node of box2Tree) {
      if (node.record.isFolder) ids.add(node.record.id);
      for (const child of node.children) {
        if (child.record.isFolder) ids.add(child.record.id);
      }
    }
    setBox2ExpandedIds(ids);
  }, [hasFamily, box2Tree, isLoadingFamilyFiles]);

  const handleBox1ToggleExpand = (id: string) => {
    setBox1ExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBox2ToggleExpand = (id: string) => {
    setBox2ExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const searchTerm = searchValue.trim().toLowerCase();

  const box1SearchIds = searchTerm ? filterTreeIds(box1Tree, searchTerm) : null;

  let box1VisibleRows: FlatRow[] = [];
  if (box1SearchIds) {
    box1VisibleRows = flattenVisible(box1Tree, box1SearchIds).filter(r =>
      box1SearchIds.has(r.record.id),
    );
  } else {
    box1VisibleRows = flattenVisible(box1Tree, box1ExpandedIds);
  }

  const box2SearchIds = hasFamily && searchTerm ? filterTreeIds(box2Tree, searchTerm) : null;

  let box2VisibleRows: FlatRow[] = [];
  if (hasFamily) {
    if (box2SearchIds) {
      box2VisibleRows = flattenVisible(box2Tree, box2SearchIds).filter(r =>
        box2SearchIds.has(r.record.id),
      );
    } else {
      box2VisibleRows = flattenVisible(box2Tree, box2ExpandedIds);
    }
  }

  const treeSearchResultCount = (() => {
    if (!searchTerm) return filteredFiles.length;
    const box1Count = box1VisibleRows.filter(r => !r.record.isFolder).length;
    const box2Count = box2VisibleRows.filter(r => !r.record.isFolder).length;
    return box1Count + box2Count;
  })();

  const { createLink } = useFileTemporaryLinkMutation();

  const {
    selection,
    selectedRecords,
    bulkDeleteNames,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  } = useBulkDocumentSelection({
    records: allOtherFiles,
    entityName: 'file',
    nameOf: record => record.file.fileName,
    deleteRecord: record => deleteFile(record.id),
    downloadRecord: record => handleDownloadFile(record),
    refetch: refetchFiles,
    logContext: { clientId },
  });

  const { openPicker: openSharepointPicker, isUploadingToSharepoint } = useSharepointUpload({
    clientId,
    onComplete: selection.exit,
  });

  const handleSharepointUploadOpen = () => {
    const filesToUpload = selectedRecords
      .filter(r => !r.isFolder)
      .map(r => ({ fileId: r.file.id, fileName: r.file.fileName }));
    openSharepointPicker(filesToUpload);
  };

  const handleDeletePress = (file: UploadedFile) => {
    resetDeleteError();
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        e,
        { clientId, fileId: fileToDelete.id },
      );
    }
    setFileToDelete(null);
  };

  const audioPlayer = useDocumentAudioPlayer();

  const handleDownloadFile = async (uploadedFile: UploadedFile) => {
    try {
      await downloadViaAnchor(createLink, uploadedFile.file.id, uploadedFile.file.fileName);
    } catch (error) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const handleDownloadPress = (file: UploadedFile) => {
    if (isAudioFile(file.file.fileType, file.file.fileName)) {
      audioPlayer.download(file, clientId);
      return;
    }
    handleDownloadFile(file);
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const handleOpenCreateFolder = () => {
    setCreateFolderParent(null);
    openCreateFolder();
  };

  const handleCreateSubfolderClick = (folder: UploadedFile) => {
    setCreateFolderParent(folder);
    openCreateFolder();
  };

  const handleCloseCreateFolder = () => {
    closeCreateFolder();
    setCreateFolderParent(null);
  };

  const handleCreateFolder = async (folderName: string) => {
    const parent = createFolderParent;
    try {
      await createFolder(folderName, parent?.id);
      if (parent) {
        setBox1ExpandedIds(prev => new Set(prev).add(parent.id));
      }
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to create folder',
        'An error occurred while trying to create the folder. Please try again later.',
        error,
        { clientId, folderName, parentId: parent?.id },
      );
      throw error;
    }
  };

  const handleRenameFolderClick = (folder: UploadedFile) => {
    setSelectedFolderForRename(folder);
    openRenameModal();
  };

  const handleRenameFolder = async (folder: UploadedFile, newName: string) => {
    await renameFolder(folder, newName);
    await refetchFiles();
  };

  const handleUploadClick = () => {
    navigate(`/client/${clientId}/document/add`);
  };

  const renderFileRenameSuffix = (file: UploadedFile) =>
    !file.isFolder && !('categoryScope' in file) ? (
      <RenameDocumentControl
        fileId={file.file.id}
        currentName={file.file.fileName}
        variant="plain"
      />
    ) : null;

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  const isPageLoading = isLoadingFiles || (hasFamily && isLoadingFamilyFiles);

  if (isPageLoading) {
    return <PageLoader absolute={false} />;
  }

  const box2Section = hasFamily ? (
    <FilesTable<UploadedFile>
      entitiesWithFile={box2VisibleRows.map(r => r.record) as UploadedFile[]}
      isLoading={false}
      additionalColumns={filedDocumentColumns}
      treeRows={box2VisibleRows}
      onToggleExpand={handleBox2ToggleExpand}
      inlineActions={file => (
        <FiledDocumentInlineActions file={file} onDownload={handleDownloadPress} />
      )}
      onClick={() => {}}
      readOnly
      variant="compact"
      nameColumnTitle="Document name"
    />
  ) : undefined;

  return (
    <FileUploadLayout
      title={selection.isSelecting ? 'Select the files' : 'Filed documents'}
      variant="compact"
      collapsibleSearch={!selection.isSelecting}
      selectedIds={selection.isSelecting ? selection.selectedIds : undefined}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={treeSearchResultCount}
      searchPlaceholder="Search documents"
      actions={
        selection.isSelecting ? (
          <SelectionHeaderActions
            selectedCount={selectedRecords.length}
            onDeleteSelected={requestBulkDelete}
            onDownloadSelected={downloadSelected}
            onUploadToSharepoint={
              flagEnabledSharepointUpload ? handleSharepointUploadOpen : undefined
            }
            onCancel={selection.exit}
            isDownloading={isBulkDownloading}
            isUploadingToSharepoint={isUploadingToSharepoint}
            showSharepointUpload={flagEnabledSharepointUpload}
          />
        ) : (
          <FiledDocumentsHeaderActions
            onCreateFolder={handleOpenCreateFolder}
            onUpload={handleUploadClick}
          />
        )
      }
      secondarySection={box2Section}
      secondaryTitle={hasFamily ? 'Family files' : undefined}
    >
      <FilesTable<UploadedFile>
        entitiesWithFile={box1VisibleRows.map(r => r.record) as UploadedFile[]}
        isLoading={isLoadingFiles}
        additionalColumns={filedDocumentColumns}
        treeRows={box1VisibleRows}
        onToggleExpand={handleBox1ToggleExpand}
        nameSuffix={renderFileRenameSuffix}
        inlineActions={file => (
          <FiledDocumentInlineActions file={file} onDownload={handleDownloadPress} />
        )}
        onClick={file => {
          if (!file.isFolder) return;
        }}
        enableDragDrop
        actions={file =>
          buildFiledDocumentRowActions(file, {
            onPlay: f => audioPlayer.play(f.file.id, f.file.fileName),
            onCreateSubfolder: handleCreateSubfolderClick,
            onRenameFolder: handleRenameFolderClick,
            onMove: handleFileDocument,
            onDelete: handleDeletePress,
            onSelect: f => selection.enter(f.id),
            onUploadToSharepoint: flagEnabledSharepointUpload
              ? f => openSharepointPicker([{ fileId: f.file.id, fileName: f.file.fileName }])
              : undefined,
          })
        }
        variant="compact"
        nameColumnTitle="Document name"
        selection={
          selection.isSelecting
            ? {
                selectedIds: selection.selectedIds,
                onToggle: selection.toggle,
                isSelectable: record => !record.isFolder,
              }
            : undefined
        }
      />

      <CreateFolderModal
        opened={createFolderOpened}
        onClose={handleCloseCreateFolder}
        onCreateFolder={handleCreateFolder}
        parentFolderName={createFolderParent?.file.fileName}
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files}
          rootLabel="Filed documents"
          onFileMoved={handleMoveFile}
        />
      )}

      {selectedFolderForRename && (
        <RenameFolderModal
          opened={renameModalOpened}
          onClose={closeRenameModal}
          folder={selectedFolderForRename}
          onRename={handleRenameFolder}
        />
      )}

      <ConfirmDeleteModal
        opened={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={fileToDelete?.isFolder ? 'Delete folder' : 'Delete file'}
        message={`Are you sure you want to delete "${fileToDelete?.file.fileName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />

      <BulkDeleteConfirmModal
        fileNames={bulkDeleteNames}
        entityName="file"
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />

      {audioPlayer.isVisible && (
        <ContentPaneOverlay>
          <AudioPlayer
            isPlaying={audioPlayer.isPlaying}
            currentTime={audioPlayer.currentTime}
            duration={audioPlayer.duration}
            isMuted={audioPlayer.isMuted}
            onPlayPause={audioPlayer.handlePlayPause}
            onSeek={audioPlayer.handleSeek}
            onMuteToggle={audioPlayer.handleMuteToggle}
            fileName={audioPlayer.fileName}
            onClose={audioPlayer.handleClose}
          />
        </ContentPaneOverlay>
      )}
    </FileUploadLayout>
  );
}
